// Phase APP-2 · Reject modal — disabled-until-valid (no race to the 409 path).
import { useState } from "react";
import { X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../services/api";

const ORANGE = "#FF6B00";
const CATEGORIES = [
  { value: "incomplete_info",   label: "Incomplete info" },
  { value: "duplicate_request", label: "Duplicate request" },
  { value: "invalid_details",   label: "Invalid details" },
  { value: "out_of_policy",     label: "Out of policy" },
  { value: "capacity_full",     label: "Capacity full" },
  { value: "other",             label: "Other" },
];
const MIN = 10, MAX = 500;

export default function RejectApprovalModal({ open, onClose, request, onDone }) {
  const [category, setCategory] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const len = remarks.length;
  const remarksValid = len >= MIN && len <= MAX;
  const canSubmit = !!category && remarksValid && !busy;

  if (!open || !request) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/approvals/${request.request_id}/reject`,
                                  { category, remarks: remarks.trim() });
      const status = r.data?.status;
      toast.success(status === "rejected_terminal"
                       ? "Rejected (terminal). No further resubmits allowed."
                       : "Rejected — submitter may resubmit once.");
      onDone?.(status);
      onClose?.();
      setCategory(""); setRemarks(""); setBusy(false);
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || "Failed";
      setErr(typeof detail === "string" ? detail : (detail.message || JSON.stringify(detail)));
      setBusy(false);
    }
  };

  const entityTitle = request?.approval_type === "packer_request"
    ? "Packer Request" : "Micro Hub Suggestion";
  const entityName = (request?.entity_payload || {}).name
                      || (request?.entity_payload || {}).full_name
                      || request?.display_name
                      || request?.request_id;
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-2"
         data-testid="reject-approval-modal" role="dialog" aria-modal="true">
      <form onSubmit={submit}
            className="bg-white w-full sm:max-w-md rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <h3 className="text-base font-semibold flex-1">Reject {entityTitle}</h3>
          <button type="button" onClick={onClose}
                  aria-label="Close" data-testid="reject-modal-close"
                  className="p-1.5 rounded-full hover:bg-slate-100">
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm text-slate-600">
            Rejecting <span className="font-semibold">{entityName}</span>.
          </div>
          {err && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 inline-flex items-start gap-1.5"
                 data-testid="reject-modal-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/> {err}
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Category <span className="text-rose-500">*</span>
            </label>
            <select value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="reject-modal-category"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#FF6B00] focus:outline-none">
              <option value="">— select category —</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Remarks <span className="text-rose-500">*</span>
              <span className="ml-2 normal-case text-slate-400">(min {MIN}, max {MAX})</span>
            </label>
            <textarea value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={4}
                      placeholder="Why is this being rejected? Be specific so the submitter can address it."
                      data-testid="reject-modal-remarks"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-[#FF6B00] focus:outline-none resize-none"/>
            <div className={"text-[10px] mt-0.5 text-right " +
                              (remarksValid ? "text-slate-400"
                                : "text-rose-600 font-semibold")}
                 data-testid="reject-modal-counter">
              {len} / {MAX} {len < MIN && `· ${MIN - len} more required`}
              {len > MAX && ` · ${len - MAX} over limit`}
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
                  data-testid="reject-modal-cancel"
                  className="text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit"
                  disabled={!canSubmit}
                  data-testid="reject-modal-submit"
                  data-can-submit={canSubmit ? "true" : "false"}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : null}
            Reject
          </button>
        </div>
      </form>
    </div>
  );
}
